import { useEffect, useState, useRef } from "react";
import { Checkbox, Radio, Empty, InputNumber, Spin } from "antd";
import { useSelector } from "react-redux";
import _ from "lodash";
import "../ev-home-page.css";
import { useActions } from "../../../core-units/hooks/useActions";
import { InspectionStepTitle } from "../../../core-units/components/EvCommonUtils";
const StepInspection = ({ validationHandler, askingPriceValidations }) => {
  /* -------------------- Redux -------------------- */
  const evServices = useSelector((state) => state.services.serviceList);
  const evProducts = useSelector((state) => state.services.productList);

  const { servicesList, servicesView, productsList, productsView } =
    useActions();

  /* -------------------- State -------------------- */
  const [evValue, setEvValue] = useState(1);
  const [evDiffLoc, setEvDiffLoc] = useState(0);
  const [evService, setEvService] = useState({});
  const [evProduct, setEvProduct] = useState({});
  const [evTotal, setEvTotal] = useState(0);
  const [evPrice, setEvPrice] = useState(0);
  const [evLoading, setEvLoading] = useState(false);
  const [showEvAddons, setShowEvAddons] = useState(false);
  const [evName, setEvName] = useState("");
  /* -------------------- componentDidMount -------------------- */
  useEffect(() => {
    // If services aren't loaded yet, fetch them so `serviceList` is populated
    if (!_.get(evServices, "data")) {
      servicesList();
    }

    const retrieveServiceStr = localStorage.getItem("evService");
    const retrieveProductStr = localStorage.getItem("evProduct");

    const retrieveService = retrieveServiceStr
      ? JSON.parse(retrieveServiceStr)
      : {};
    let retrieveProduct = [];
    if (retrieveProductStr) {
      try {
        retrieveProduct = JSON.parse(retrieveProductStr);
      } catch (e) {
        retrieveProduct = retrieveProductStr;
      }
    }

    // Always restore saved values on mount so addons/total reappear after refresh/back
    setEvDiffLoc(Number(localStorage.getItem("evDiffLoc")) || 0);
    setEvValue(_.parseInt(localStorage.getItem("evValue")) || 0);
    setEvPrice(
      localStorage.getItem("evPrice")
        ? Number(localStorage.getItem("evPrice"))
        : 0,
    );
    setEvTotal(
      localStorage.getItem("evTotal")
        ? Number(localStorage.getItem("evTotal"))
        : 0,
    );
    setEvService(retrieveService || {});
    setEvProduct(retrieveProduct || []);
    setEvName(localStorage.getItem("selectedEvValuelocation") || "");

    const hasPreviousSelection = !_.isEmpty(retrieveService);
    console.log("hasPreviousSelection", hasPreviousSelection);

    setShowEvAddons(Boolean(hasPreviousSelection));
    setEvLoading(false);

    // If a service was previously selected, fetch its full product list
    // so all available addons are shown (not just the stored selected ones).
    const restoredServiceId =
      _.get(retrieveService, "id") ||
      _.parseInt(localStorage.getItem("evValue"));
    if (restoredServiceId) {
      const queryParams = { "filter[service_id]": restoredServiceId };
      setEvLoading(true);
      productsList(queryParams, () => {
        setEvLoading(false);
      });
    }
  }, []);

  /* -------------------- componentDidUpdate -------------------- */
  const isInitialMount = useRef(true);
  useEffect(() => {
    const lsDiff = Number(localStorage.getItem("evDiffLoc"));
    if (isInitialMount.current) {
      // skip clearing logic on initial mount; mount effect restores saved values
      isInitialMount.current = false;
    } else {
      if (evDiffLoc !== lsDiff) {
        setEvDiffLoc(lsDiff);
        setEvProduct([]);
        setEvTotal(Number(localStorage.getItem("evTotal")) || 0);
        setEvPrice(Number(localStorage.getItem("evPrice")) || 0);
        setShowEvAddons(false);
      }
    }

    if (localStorage.getItem("evProduct")) {
      validationHandler?.();
    }

    askingPriceValidations?.(localStorage.getItem("evProduct"));

    if (!localStorage.getItem("evProduct")) {
      localStorage.removeItem("evPrice");
    }
  }, [evDiffLoc]);

  /* -------------------- ensure addons visible when service is selected -------------------- */
  useEffect(() => {
    if (!showEvAddons && !_.isEmpty(evService) && evDiffLoc < 120) {
      setShowEvAddons(true);
    }
  }, [evService, evDiffLoc, showEvAddons]);

  /* -------------------- evService Change -------------------- */
  const onChange = async (e) => {
    const evServiceId = e.target.value;

    // Require a selected location before allowing service selection
    if (!localStorage.getItem("selectedEvValuelocation")) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setEvLoading(true);
    setShowEvAddons(false);
    validationHandler?.();
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });

    // update local state immediately so Radio shows as selected
    setEvValue(evServiceId);

    const queryParams = { "filter[service_id]": evServiceId };

    servicesView({ id: evServiceId }, (value) => {
      setEvService(value);
      setEvTotal(_.get(value, "price"));
      localStorage.setItem("evService", JSON.stringify(value));
      localStorage.setItem("evTotal", _.get(value, "price"));
    });

    productsList(queryParams, () => {
      setEvLoading(false);
      setShowEvAddons(true);
    });

    localStorage.setItem("evValue", evServiceId);
  };

  /* -------------------- SERVICES LIST -------------------- */
  const servicesData = () => {
    const serviceData = _.get(evServices, "data", []);
    const loc = parseFloat(localStorage.getItem("evDiffLoc"));
    const renderCards = (data) =>
      _.map(data, (val, key) => (
        <div className="col-9 c ol-md-4 col-lg-3" key={key}>
          <div className="EvPricingCard">
            <Radio value={val.id}>
              <span className="EvPricingCardInfo">
                <span className="EvPricingCardAmount">
                  $ {_.get(val, "price", "-")}
                </span>
                <span className="EvPricingCardPlan">
                  {_.get(val, "name", "-")}
                </span>
              </span>
            </Radio>
          </div>
        </div>
      ));
    let filteredServices = [];
    if (loc <= 40) {
      filteredServices = serviceData.filter((s) => [16, 17].includes(s.id));
    } else if (loc > 40 && loc <= 70) {
      filteredServices = serviceData.filter((s) => [18, 19].includes(s.id));
    } else if (loc > 70 && loc <= 100) {
      filteredServices = serviceData.filter((s) => [20].includes(s.id));
    } else if (loc > 100) {
      return (
        <Empty
          description={
            <strong>
              There is no inspection available in the location you have entered.
            </strong>
          }
        />
      );
    } else {
      filteredServices = serviceData.filter((s) => [16, 17].includes(s.id));
    }
    return (
      <div className="EvPricingCardSection" id="sample">
        <Radio.Group onChange={onChange} value={evValue}>
          <div className="row justify-content-center">
            {renderCards(filteredServices)}
          </div>
        </Radio.Group>
      </div>
    );
  };

  /* -------------------- PRODUCT CHANGE -------------------- */
  const productchange = (val) => {
    let products = [];

    if (val.length === 0) {
      setEvTotal(evService.price);
      setEvProduct(val);
      localStorage.setItem("evProduct", JSON.stringify(val));
      localStorage.setItem("evTotal", evService.price);
      return;
    }

    _.map(val, (each) => {
      // `each` comes from Checkbox values (strings). Ensure numeric id for API.
      const id = Number(each);
      productsView({ id }, (value) => {
        products.push(value);
        const addonPrice = _.sumBy(products, "price");
        setEvProduct(products);
        setEvTotal(evService.price + addonPrice);
        localStorage.setItem("evProduct", JSON.stringify(products));
        localStorage.setItem("evTotal", evService.price + addonPrice);
      });
    });
  };

  /* -------------------- TOTAL PRICE -------------------- */
  const totalPrice = () => (
    <div className="EvCouponCodeTotalSection">
      <span>Total</span>
      <span className="EvCouponCodeTotalAmount">$ {evTotal}</span>
    </div>
  );

  /* -------------------- ADDONS LIST -------------------- */
  const productList = () => {
    const products = _.get(evProducts, "data", evProduct);

    if (evLoading === true) {
      return (
        <div className="example">
          <Spin />
        </div>
      );
    } else {
      return (
        <div className="EvPricingCardSection">
          <Checkbox.Group
            onChange={productchange}
            defaultValue={_.map(evProduct, (p) =>
              String(_.get(p, "product.id", p.id)),
            )}
          >
            <div className="row justify-content-center">
              {_.map(products, (val, key) => {
                const item = _.get(val, "product", val);
                return (
                  <div className="col-9 col-md-4 col-lg-3" key={key}>
                    <div className="EvPricingCard">
                      <Checkbox
                        value={String(_.get(item, "id"))}
                        className={
                          _.get(item, "name") === "Car Valuation Report"
                            ? "ant-card-wrapper"
                            : _.get(item, "name") ===
                                "Car History Report (PPSR Included)"
                              ? "ant-card-wrapper-car-history"
                              : ""
                        }
                      >
                        <span className="EvPricingCardInfo">
                          <span className="EvPricingCardAmount">
                            $ {_.get(item, "price")}
                          </span>
                          <span className="EvPricingCardPlan">
                            {_.get(item, "name")}
                          </span>
                        </span>
                      </Checkbox>
                    </div>
                  </div>
                );
              })}
            </div>
          </Checkbox.Group>
        </div>
      );
    }
  };

  /* -------------------- ADDONS LABEL -------------------- */
  const addonsData = () =>
    !_.isEmpty(evService) && (
      <>
        <div className="EvPricingAddOnTitle">Add Ons</div>
        <div className="EvPricingAddOnSubTitle">
          {evService.name} <b>$ {evService.price}</b>
        </div>
        <div className="EvPricingAddOnDesc">
          SafeBuy recommend below add ons for more peace of mind
        </div>
      </>
    );

  /* -------------------- PRICE CHANGE -------------------- */
  const onPriceChange = (val) => {
    setEvPrice(val);
    localStorage.setItem("evPrice", val);
  };

  const priceValidation = () => {
    return _.map(evProduct, (value, key) => {
      const id = _.get(value, "id", _.get(value, "product.id"));
      if (id === 4) {
        return (
          <div
            className="row justify-content-center"
            style={{ width: 100 + "%", float: "left" }}
          >
            <div className="col-12 col-md-5 col-lg-5">
              <div className="EvPricingAddOnDesc font-weight-bold">
                Please enter vehicle asking price by seller
              </div>
              <div className="EvCouponCodeInputSection">
                <div className="row justify-content-center">
                  <div className="col-12 col-md-10 col-lg-4">
                    <InputNumber
                      defaultValue={evPrice}
                      min={1}
                      formatter={(value) =>
                        `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                      onChange={onPriceChange}
                      className="InputNumberPrice w-100"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }
    });
  };
  console.log("askingPriceValidations", showEvAddons);
  /* -------------------- RENDER -------------------- */
  return (
    <div className="EvInspectionStepsContainer">
      {InspectionStepTitle()}
      {servicesData()}
      {parseFloat(localStorage.getItem("evDiffLoc")) < 120 && (
        <div className="EvPricingAddOnSection" key={_.uniqueId()}>
          {showEvAddons && addonsData()}
          {showEvAddons && productList()}
          {showEvAddons && priceValidation()}
          <div className="row justify-content-center w-100">
            <div className="col-12 col-md-5 col-lg-5 text-center">
              <div className="EvCouponCodeTotal">{totalPrice()}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StepInspection;
